import { TrashSimpleIcon, WarningIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import invariant from 'tiny-invariant'
import { DefaultButton } from '../../../components/buttons/default-button.tsx'
import { GhostButton } from '../../../components/buttons/ghost-button.tsx'
import { useShowMutationError } from '../../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../../services/toast-service.tsx'
import { useDeleteIntegration } from '../../../utils/api-client/api-hooks.ts'
import type { Integration } from '../../../utils/api-client/api-schemas.ts'
import * as styles from './delete-integration-modal.css.ts'
import { IntegrationBrandIcon } from './integration-icon.tsx'
import { getInstanceName, getProviderLabel } from './integration-instance-utils.ts'

export type DeleteIntegrationModalProps = {
  integration: Integration
  onCancel: () => unknown
  onSuccess: () => unknown
}

// The standard delete-confirmation modal (mirrors the coding-guidelines / repository patterns): owns
// the delete mutation, toasts the failure (including the in-use 409 detail) via useShowMutationError,
// and toasts + calls onSuccess on deletion.
export const DeleteIntegrationModal: React.FC<DeleteIntegrationModalProps> = ({ integration, onCancel, onSuccess }) => {
  const { addToast } = useToastService()

  const showDeleteError = useShowMutationError({ prefix: 'Failed to delete integration' })
  const deleteIntegration = useDeleteIntegration({
    onSuccess: () => {
      addToast({ message: <>{getInstanceName(integration)} removed.</>, variant: 'success' })
      onSuccess()
    },
    onError: showDeleteError,
  })

  const toolLabel = getProviderLabel(integration.type)
  const instanceName = getInstanceName(integration)

  const handleDelete = () => {
    invariant(integration.id, 'A configured integration always has an id')
    deleteIntegration.mutate({ integrationId: integration.id })
  }

  return (
    <Dialog.Root open onOpenChange={open => (!open ? onCancel() : undefined)}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        {/* A confirmational modal must not dismiss on an outside click — losing a destructive
            confirm to a stray click is the failure the pattern exists to prevent. */}
        <Dialog.Content className={styles.content} onPointerDownOutside={event => event.preventDefault()}>
          <Dialog.Title className={styles.title}>
            <WarningIcon size={28} weight="light" className={styles.icon} />
            Delete integration?
          </Dialog.Title>
          <Dialog.Description asChild>
            <p className={styles.description}>You are about to delete the following integration:</p>
          </Dialog.Description>
          <div className={styles.summaryCard}>
            <div className={styles.summaryHeader}>
              <IntegrationBrandIcon integration={integration} />
              <span>{toolLabel}</span>
            </div>
            {instanceName && <p className={styles.summaryName}>{instanceName}</p>}
          </div>
          <p className={styles.description}>This action cannot be undone. Do you wish to proceed?</p>
          <div className={styles.buttons}>
            <GhostButton
              size="small"
              onClick={onCancel}
              state={deleteIntegration.isPending ? 'disabled' : 'default'}
              className={styles.cancelBtn}
            >
              Cancel
            </GhostButton>
            <DefaultButton
              buttonType="destructive"
              size="small"
              state={deleteIntegration.isPending ? 'loading' : 'default'}
              onClick={handleDelete}
              loadingLabel="Deleting…"
              icon={<TrashSimpleIcon size={14} weight="regular" />}
            >
              Delete integration
            </DefaultButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
